import React, { useEffect, useState } from 'react';
import { useBranches, useCreateClient, useUpdateClient } from '@/hooks/use-api';
import { useRoleStore } from '@/lib/store';
import type { SusuClient } from '@shared/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

interface ClientFormDialogProps {
  client?: SusuClient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClientFormDialog({ client, open, onOpenChange }: ClientFormDialogProps) {
  const role = useRoleStore((s) => s.role);
  const isAdmin = role === 'Admin';
  const { data: branches } = useBranches(isAdmin);

  const [form, setForm] = useState({ name: '', phone: '', territory: '', branchId: '' });
  const [isFlagged, setIsFlagged] = useState(false);

  const createClient = useCreateClient();
  const updateClient = useUpdateClient();

  const isEditing = !!client;
  const isManager = role === 'Manager' || role === 'Officer';

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || '',
        phone: client.phone || '',
        territory: client.territory || '',
        branchId: client.branchId || '',
      });
      setIsFlagged(client.isAtRisk || false);
    } else {
      setForm({ name: '', phone: '', territory: '', branchId: '' });
      setIsFlagged(false);
    }
  }, [client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error('Name and phone are required');
      return;
    }

    try {
      if (isEditing && client) {
        await updateClient.mutateAsync({
          id: client.id,
          data: {
            name: form.name,
            phone: form.phone,
            territory: form.territory,
            branchId: form.branchId,
            isAtRisk: isFlagged,
          },
        });
        toast.success(`Client ${form.name} updated successfully`);
      } else {
        await createClient.mutateAsync({
          name: form.name,
          phone: form.phone,
          territory: form.territory,
          branchId: form.branchId || undefined,
        });
        toast.success(`Client ${form.name} created successfully`);
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} client`);
    }
  };

  const isPending = createClient.isPending || updateClient.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[2rem] border-border p-0 sm:max-w-lg">
        <DialogHeader className="border-b border-border bg-muted/30 p-6">
          <DialogTitle className="text-xl font-black uppercase tracking-tight text-foreground">
            {isEditing ? 'Edit Client' : 'Register New Client'}
          </DialogTitle>
          <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {isEditing ? 'Update client information and settings.' : 'Enter client details to register them in the system.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="space-y-2">
            <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Full Name</Label>
            <Input
              placeholder="Client full name"
              value={form.name}
              onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))}
              className="h-12 rounded-xl border-border bg-muted/30 font-bold"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Phone Number</Label>
            <Input
              placeholder="e.g. 0241234567"
              value={form.phone}
              onChange={(e) => setForm((c) => ({ ...c, phone: e.target.value }))}
              className="h-12 rounded-xl border-border bg-muted/30 font-bold"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Territory</Label>
              <Input
                placeholder="e.g. Central Market"
                value={form.territory}
                onChange={(e) => setForm((c) => ({ ...c, territory: e.target.value }))}
                className="h-12 rounded-xl border-border bg-muted/30 font-bold"
              />
            </div>

            {isAdmin && (
              <div className="space-y-2">
                <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Branch</Label>
                <Select value={form.branchId} onValueChange={(v) => setForm((c) => ({ ...c, branchId: v }))}>
                  <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30">
                    <SelectValue placeholder="Select Branch" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {branches?.map((b) => (
                      <SelectItem key={b.id} value={b.id} className="font-bold">
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {isEditing && (
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/30 p-4">
              <input
                type="checkbox"
                checked={isFlagged}
                onChange={(e) => setIsFlagged(e.target.checked)}
                className="h-5 w-5 rounded border-border text-destructive focus:ring-destructive"
              />
              <div>
                <p className="font-black text-foreground text-sm">Flag for Follow-up</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Mark client as requiring special attention.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-12 rounded-xl px-6 text-xs font-black uppercase text-muted-foreground"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="h-12 rounded-xl bg-primary px-8 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90"
            >
              {isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isEditing ? 'Save Changes' : 'Register Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
